import Template, {ImageValue} from '../models/Template';

/**
 * Per-inputKey render fit for the scene engine. Only one value exists today:
 * 'contain' — draw the whole image inside the field box, letterboxed with
 * transparent margins, instead of cover-cropping it. The absence of a key
 * means the template's own fit (cover, or the element's `scaleMode: 'FIT'`).
 */
export type ImageFitMap = { [inputKey: string]: 'contain' };

/**
 * Compare-only normalization of an asset URL. Presigned / cache-busted
 * variants of the SAME object differ only in the query string, and a raw
 * comparison of those would call an untouched default "replaced" — which
 * would silently switch a whole template to contain-fit. Never used for
 * fetching, only for identity.
 */
export function normalizeImageUrl(url: unknown): string {
    if (typeof url !== 'string') return '';
    const trimmed = url.trim();
    const cut = trimmed.search(/[?#]/);
    return cut === -1 ? trimmed : trimmed.slice(0, cut);
}

/** The asset an image value came from. A template DEFAULT carries `assetId`
 *  (the API stamps it — see ImageInputDefault in template_model.py); a value
 *  built straight from a gallery Asset may carry the Asset's `id` instead. */
function assetIdentity(value: unknown): string | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const raw = (value as { assetId?: unknown }).assetId ?? (value as { id?: unknown }).id;
    return typeof raw === 'string' && raw ? raw : undefined;
}

/** Same asset? Ids decide only when BOTH sides carry one — either side can be
 *  missing it (a legacy default, an Excel row, an older draft), and an
 *  id-vs-nothing comparison would call every untouched default "replaced".
 *  Otherwise the normalized URL decides, so re-picking the very asset that is
 *  already the default is NOT a replacement. */
function sameImage(a: unknown, b: unknown): boolean {
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
    const idA = assetIdentity(a);
    const idB = assetIdentity(b);
    if (idA && idB) return idA === idB;
    const urlA = normalizeImageUrl((a as ImageValue).highRes);
    return !!urlA && urlA === normalizeImageUrl((b as ImageValue).highRes);
}

/** Videos never contain-fit: the MP4 path composites through FFmpeg (its own
 *  contain + blur fill) and never goes through the Konva image branch.
 *
 *  MIME **or** extension, never MIME-as-veto: asset records carry both and
 *  either can be wrong or absent (an Excel row has no `assetType`, an upload
 *  can land as `application/octet-stream`), so a non-video MIME must not
 *  override a `.mp4` URL. The URL half mirrors sceneToKonva's `isVideoUrl` so
 *  both render ports agree on what a video is. */
function isVideoValue(value: ImageValue): boolean {
    const mime = typeof value.type === 'string' ? value.type.trim().toLowerCase() : '';
    return mime.startsWith('video/')
        || /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(value.highRes || '');
}

/**
 * Which image fields the USER replaced, and therefore render contained.
 *
 * Derived, never stored: "the user picked this image" is image-value state, so
 * it is computed in this one place from (inputs, selections) and every entry
 * point — gallery pick, upload, Fill Mode quick upload, AI edit result, Excel
 * import, draft hydration — is covered without touching any of them. Keyed by
 * inputKey (not element id) so a key painted by several elements contains in
 * all of them.
 *
 * A missing default counts as replaced: there is nothing the user's pick could
 * have been designed to fit.
 */
export function deriveImageFit(
    inputs: Template['inputs'] | undefined,
    selections: { [key: string]: string | ImageValue } | undefined,
): ImageFitMap {
    const out: ImageFitMap = {};
    if (!inputs || !selections) return out;
    for (const key of Object.keys(inputs)) {
        const input = inputs[key];
        if (!input || input.type !== 'image') continue;
        const value = selections[key];
        // A text value (or nothing chosen) is not an image replacement.
        if (!value || typeof value !== 'object') continue;
        if (isVideoValue(value)) continue;
        if (sameImage(value, input.default)) continue;
        out[key] = 'contain';
    }
    return out;
}
